// ============================================================================
// finAuditoriaApi — cliente das telas da Auditoria (Financeiro, pacote 1,
// 07/10/2026)
// ============================================================================
// Dois endpoints, os dois só do OPERADOR financeiro (o servidor confere em
// toda chamada; sem sessão = 401, outra conta = 403):
//   · /api/fin (finGet/finPost de finApi.js): alertas, contagem, coletas,
//     catálogo, "coletar agora", recebimentos e taxas; entradas do Avec por
//     dia e a taxa Stone esperada por mês (Fluxo de Caixa);
//   · /api/fin-extrato (chamarExtrato, MESMA semântica do finApi): contas,
//     arquivos, linhas, envio do OFX e decisões débito × título.
// REGRA LINEAR: falhou → lança FinApiErro (mensagem em português, status,
// código e o corpo em .dados). Nunca devolve "vazio" no lugar de um erro.
// Nada aqui grava em cash_movements: "Lançar em Contas a Pagar" abre o
// formulário existente, que grava só no Salvar dele.
// 09/10/2026 — "quando lançar no Contas a Pagar o sistema deve entender e
// SUMIR a conta como pendente": o Salvar do formulário vindo do extrato chama
// lancarDoExtrato (op `lancar` do /api/fin-extrato: o servidor cria o título E
// grava o vínculo com a linha na MESMA transação). Antes da financeiro-009
// (503 migracao_pendente) cai no caminho antigo, que o FORMULÁRIO injeta
// (criarTituloAntigo) — este arquivo continua sem gravar título — e liga a
// linha em seguida (decidir vincular_titulo). Depois de qualquer gravação, as
// telas abertas recebem o evento EVENTO_EXTRATO (o Extrato Stone recarrega).
// ============================================================================

import { finGet, finPost, FinApiErro } from './finApi.js';
import { sessionToken } from './secureRest.js';
import { filtrosParaQuery, motivoValido, MIN_MOTIVO } from './finAuditoriaFormat.js';

export { FinApiErro };

/** Mesmo teto do servidor (api/_lib/fin-extrato-ofx.js LIMITE_BYTES). */
export const LIMITE_EXTRATO_BYTES = 3 * 1024 * 1024;
const BASE_EXTRATO = '/api/fin-extrato';
const ACOES_EXTRATO = new Set(['vincular_titulo', 'ignorar', 'desfazer']);
const STATUS_ALERTA = new Set(['aberto', 'resolvido', 'justificado', 'ignorado']);

// Parâmetros de GET sem null/undefined/'' (o servidor recusa "conta_id=").
function limparParams(params) {
  const out = {};
  for (const [k, v] of Object.entries(params || {})) {
    if (v === undefined || v === null || v === '') continue;
    out[k] = String(v);
  }
  return out;
}

/**
 * /api/fin-extrato com a MESMA semântica do finApi (x-session-token,
 * cache no-store, erro em FinApiErro com status/código/corpo).
 */
export async function chamarExtrato(method, params, { fetchImpl } = {}) {
  const f = fetchImpl || (typeof fetch === 'function' ? fetch : null);
  const token = sessionToken();
  if (!token) throw new FinApiErro('Sessão expirada. Entre de novo.', { status: 401, code: 'sem_sessao' });
  const url = method === 'GET' ? `${BASE_EXTRATO}?${new URLSearchParams(limparParams(params))}` : BASE_EXTRATO;
  let r;
  try {
    r = await f(url, {
      method,
      headers: { 'Content-Type': 'application/json', 'x-session-token': token },
      body: method === 'GET' ? undefined : JSON.stringify(params),
      cache: 'no-store',
    });
  } catch {
    throw new FinApiErro('Sem resposta do servidor (conexão). Tente de novo em instantes.', { status: 0, code: 'rede' });
  }
  const text = await r.text().catch(() => '');
  const trimmed = String(text || '').trim();
  let j = null;
  if (trimmed && !trimmed.startsWith('<')) {
    try { j = JSON.parse(trimmed); } catch { j = null; }
  }
  if (!r.ok) {
    const padrao = r.status === 504
      ? 'O servidor demorou demais para responder (tempo esgotado). Tente de novo.'
      : r.status === 413
        ? 'O arquivo é grande demais para enviar (limite de 3 MB).'
        : `Erro ${r.status} ao falar com o servidor.`;
    throw new FinApiErro((j && typeof j.error === 'string' && j.error) || padrao, {
      status: r.status, code: (j && j.code) || `http_${r.status}`, dados: j && typeof j === 'object' ? j : null,
    });
  }
  if (!j || typeof j !== 'object') {
    throw new FinApiErro('Resposta inesperada do servidor (tente de novo).', { status: r.status, code: 'resposta' });
  }
  return j;
}

function exigirMotivo(motivo) {
  if (!motivoValido(motivo)) {
    throw new FinApiErro(`Escreva o motivo com pelo menos ${MIN_MOTIVO} letras.`, { status: 400, code: 'motivo_curto' });
  }
}

// ─── Alertas (/api/fin) ────────────────────────────────────────────────────
// Classe do aviso (lapidação, 12/10/2026): 'principal' (lista padrão, badge e
// push), 'rotina' (pendência de lançamento: só na régua), 'pista' (Fechamento
// do dia e detalhe) ou 'todas'. Sem classe no pedido = 'todas' (a régua da
// Rotina lê tudo e separa pela `classe` de cada linha). Sem ruído (22/10/2026):
// 'informativo' = o aviso que a regra reconheceu (forma trocada no Avec, venda da
// assinatura…), fora da lista principal, do número e do push.
export const CLASSES_LISTA = Object.freeze(['principal', 'rotina', 'pista', 'informativo', 'todas']);
function comClasse(q, classe) {
  const c = String(classe ?? '').trim();
  return CLASSES_LISTA.includes(c) && c !== 'todas' ? { ...q, classe: c } : q;
}
/** Filtros da tela (ver filtrosParaQuery; + classe) → { alertas (com .classe), tem_mais, proximo_antes_de_id }. */
export const listarAlertas = (filtros = {}, opts) => finGet('alertas', comClasse(filtrosParaQuery(filtros), filtros?.classe), opts);
/** { alerta, eventos, contexto, catalogo } */
export const carregarAlerta = (id, opts) => finGet('alerta', { id }, opts);
/**
 * { abertos, badge, historico_abertos, em_jogo_cents, rotina_abertos, escopo_avisos, ultima_coleta,
 *   vigilancia, coletado, gerado_em } — números só dos avisos PRINCIPAIS e do escopo da conta
 * (escopo_avisos: null = recebe tudo; ['unit-moema'] = só Moema).
 */
export const contarAlertas = (opts) => finGet('alertas-contagem', {}, opts);

/** Resolver / justificar / ignorar / reabrir — motivo obrigatório (≥ 10 letras). */
export async function mudarStatusAlerta({ id, status, motivo } = {}, opts) {
  if (!STATUS_ALERTA.has(status)) throw new FinApiErro('Status inválido.', { status: 400, code: 'status_invalido' });
  exigirMotivo(motivo);
  return finPost({ op: 'alerta-status', id, status, motivo: String(motivo).trim() }, opts);
}

/** { coletas, por_unidade } das últimas N horas (48 padrão; máx. 336). */
export const carregarColetas = (horas, opts) => finGet('auditoria-coletas', { horas }, opts);
/** { catalogo, parametros } */
export const carregarCatalogo = (opts) => finGet('auditoria-catalogo', {}, opts);
/** "Coletar agora": planeja um lote manual de hoje (o cron coleta em até 10 min). */
export const enfileirarColeta = (opts) => finPost({ op: 'auditoria-enfileirar' }, opts);

// ─── Recebimentos e taxas (/api/fin) ───────────────────────────────────────
/** { de, ate, unidades, coletado, agenda, conferencia, contas, avisos } */
export const carregarRecebimentos = ({ de, ate, unit_id } = {}, opts) => finGet('recebimentos', { de, ate, unit_id }, opts);
/** { taxas, vigentes, prazo, feriados } */
export const carregarTaxas = (opts) => finGet('taxas', {}, opts);

// ─── Entradas do Avec (07/10/2026: "vamos começar a puxar do Avec de novo") ─
/**
 * Taxa Stone esperada por unidade × mês, para o Fluxo de Caixa.
 * de/ate = 'AAAA-MM' (máx. 12 meses). → { modo:'resumo_mes', auditoria, meses: [{ unit_id,
 * competencia, fonte: 'coleta'|'parcial'|'sem_coleta'|'futuro', venda_cents, taxa_cents,
 * taxa_min_cents, taxa_max_cents, taxa_cartao_pct: {pct, status} }], avisos }
 */
export const carregarTaxasMes = ({ de, ate, unit_id } = {}, opts) => finGet('recebimentos', { resumo: 'mes', de, ate, unit_id }, opts);
/**
 * Entradas do Avec por unidade × dia de uma competência ('AAAA-MM').
 * → { coletado, cobertura, dias: [{ unit_id, dia, dinheiro_cents, pix_cents, debito_cents, credito_cents,
 * transferencia_cents, outras_cents, total_cents, taxa_cents, liquido_cents, estimado, caiu_cents|null,
 * caiu_conta_cents|null, conta_compartilhada }], totais, extrato, avisos }
 */
export const carregarEntradasAvec = ({ competencia, unit } = {}, opts) => finGet('entradas-avec', { competencia, unit }, opts);

// ─── Extrato Stone (/api/fin-extrato) ──────────────────────────────────────
export const listarContasExtrato = (opts) => chamarExtrato('GET', { op: 'contas' }, opts);
export const listarArquivosExtrato = (conta_id, opts) => chamarExtrato('GET', { op: 'arquivos', conta_id }, opts);
/** filtros: { conta_id, de, ate, tipo: 'credito'|'debito'|'todos', pendentes: boolean, limite, antes_de_id, rotina? }
 *  rotina (22/10/2026, FIN_ANTES_DA_ROTINA): com "só pendentes", a saída de antes de 08/10 fica de fora
 *  e a resposta traz antes_da_rotina { quantidade, de, ate, desde }. */
export function listarLinhasExtrato(filtros = {}, opts) {
  const { conta_id, de, ate, tipo, pendentes, limite, antes_de_id, rotina } = filtros || {};
  return chamarExtrato('GET', {
    op: 'linhas', conta_id, de, ate,
    tipo: tipo && tipo !== 'todos' ? tipo : undefined,
    pendentes: pendentes ? 1 : undefined,
    limite, antes_de_id,
    rotina: rotina === true ? 1 : undefined,
  }, opts);
}

/** Bytes → base64 (em pedaços: arquivo grande não estoura a pilha do navegador). */
export function bytesParaBase64(bytes) {
  const u8 = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes || []);
  let bin = '';
  const PASSO = 0x8000;
  for (let i = 0; i < u8.length; i += PASSO) {
    bin += String.fromCharCode.apply(null, u8.subarray(i, i + PASSO));
  }
  return btoa(bin);
}

const mb = (n) => (n / (1024 * 1024)).toLocaleString('pt-BR', { maximumFractionDigits: 1 });

/**
 * Envia o OFX: lê o File no navegador como base64 e recusa > 3 MB ANTES de
 * mandar. Respostas de erro úteis (FinApiErro.code): conta_confirmar (409,
 * .dados.acctid_mascarado — perguntar e reenviar com confirmar_conta=true),
 * arquivo_repetido (409, quem/quando em .dados), csv_ainda_nao (415).
 */
// motivo_conta (22/10/2026, FIN_EXTRATO_CONTA_CERTA): o "confirmar a conta" é só do dono, com o motivo
export async function enviarExtrato({ conta_id, arquivo, confirmar_conta = false, motivo_conta = '' } = {}, opts) {
  if (!conta_id) throw new FinApiErro('Escolha a conta Stone do extrato.', { status: 400, code: 'sem_conta' });
  if (!arquivo || typeof arquivo.arrayBuffer !== 'function') {
    throw new FinApiErro('Escolha o arquivo do extrato (OFX).', { status: 400, code: 'sem_arquivo' });
  }
  const tamanho = Number(arquivo.size) || 0;
  if (tamanho > LIMITE_EXTRATO_BYTES) {
    throw new FinApiErro(`O arquivo tem ${mb(tamanho)} MB e o limite é 3 MB. Baixe um período menor no app da Stone.`,
      { status: 413, code: 'arquivo_grande' });
  }
  if (tamanho === 0) throw new FinApiErro('O arquivo está vazio.', { status: 400, code: 'arquivo_vazio' });
  const bytes = new Uint8Array(await arquivo.arrayBuffer());
  if (bytes.length > LIMITE_EXTRATO_BYTES) {
    throw new FinApiErro(`O arquivo tem ${mb(bytes.length)} MB e o limite é 3 MB. Baixe um período menor no app da Stone.`,
      { status: 413, code: 'arquivo_grande' });
  }
  return chamarExtrato('POST', {
    op: 'enviar',
    conta_id,
    nome_arquivo: String(arquivo.name || 'extrato.ofx').slice(0, 200),
    conteudo_base64: bytesParaBase64(bytes),
    confirmar_conta: confirmar_conta === true,
    ...(confirmar_conta === true && String(motivo_conta || '').trim() ? { motivo_conta: String(motivo_conta).trim().slice(0, 300) } : {}),
  }, opts);
}

/**
 * Decisão sobre uma linha do extrato (imutável; "desfazer" cria outra):
 * vincular_titulo (só débito, com titulo_id), ignorar e desfazer (motivo ≥ 10).
 * NÃO dá baixa no título nem grava em cash_movements.
 */
export async function decidirLinhaExtrato({ linha_id, acao, titulo_id, motivo, categoria_sugerida } = {}, opts) {
  if (!ACOES_EXTRATO.has(acao)) throw new FinApiErro('Ação inválida.', { status: 400, code: 'acao_invalida' });
  if (acao === 'vincular_titulo' && !titulo_id) throw new FinApiErro('Escolha o título.', { status: 400, code: 'titulo_inexistente' });
  if (acao !== 'vincular_titulo') exigirMotivo(motivo);
  const corpo = { op: 'decidir', linha_id, acao };
  if (titulo_id) corpo.titulo_id = titulo_id;
  if (motivo && String(motivo).trim()) corpo.motivo = String(motivo).trim();
  if (categoria_sugerida) corpo.categoria_sugerida = categoria_sugerida;
  return chamarExtrato('POST', corpo, opts);
}

// ─── 09/10/2026: lançar pelo extrato já ligado; débito do título ───────────
export const EVENTO_EXTRATO = 'fin:extrato-mudou';
/** Avisa as telas abertas que uma linha do extrato mudou (só no navegador). */
export function avisarMudancaExtrato(detalhe = {}) {
  try {
    if (typeof window === 'undefined' || typeof window.dispatchEvent !== 'function' || typeof CustomEvent !== 'function') return;
    window.dispatchEvent(new CustomEvent(EVENTO_EXTRATO, { detail: detalhe }));
  } catch {
    // aviso é só conveniência: a gravação já deu certo
  }
}

const RE_ANEXO = /^private:finance\/[A-Za-z0-9_.-]{1,200}$/;
// 20/10/2026 (vales, FIN_VALES): com a categoria do vale o formulário manda também a pessoa, o mês do
// desconto (só quando a pessoa o trocou), as parcelas e a competência do comissionado; sem a flag esses
// campos nunca vêm e o pedido é o de antes. O vale NUNCA usa o caminho antigo (quem chama não injeta
// criarTituloAntigo): sem a financeiro-013 o servidor responde 503 e nada é gravado.
// Correção B2 (20/10/2026): o ok do dono (PIX de antes de 08/10/2026) vai no pedido, só como o booleano
// true (como o confirmo_nao_e_repasse); quem confere que é o master é o servidor. Sem ele o próprio dono
// levava 409 vale_pix_antigo.
const CAMPOS_LANCAR = ['unit_id', 'custo_de', 'categoria', 'titulo', 'fornecedor', 'observacao', 'forma_pagamento', 'vencimento',
  'data_competencia', 'anexo_url', 'motivo_outra_despesa', 'motivo_nao_e_repasse', 'professional_id', 'mes_desconto', 'parcelas', 'competencia'];

/**
 * Lança o título a partir de uma linha do extrato JÁ LIGADO a ela (op lancar).
 * corpo: { linha_id, unit_id, custo_de?, categoria, titulo, fornecedor?, observacao?, forma_pagamento?,
 *          vencimento?, data_competencia?, anexo_url?, confirmo_nao_e_repasse?, motivo_nao_e_repasse?,
 *          professional_id?, mes_desconto?, parcelas?, competencia?, ok_do_dono? (vale, 20/10/2026) }
 * criarTituloAntigo: () => Promise<{ id }> — o caminho antigo (só com a
 * financeiro-009 ainda não rodada; quem chama injeta o createAccountPayable).
 * → { ok, ja_existia, titulo, decisao, avisos, caminho: 'lancar'|'antigo', ligado }
 * Erros: os do servidor (ex.: 409 parece_repasse com .dados.candidatos; 22/10/2026: 409 conta_igual com
 * .dados.contas_iguais e .dados.pode_passar: o dono passa com confirmo_outra_despesa + motivo_outra_despesa).
 */
export async function lancarDoExtrato(corpo = {}, { criarTituloAntigo = null, ...opts } = {}) {
  if (!/^\d{1,18}$/.test(String(corpo?.linha_id ?? ''))) {
    throw new FinApiErro('Linha do extrato sem identificação (atualize a tela).', { status: 400, code: 'linha_inexistente' });
  }
  if (corpo.anexo_url && !RE_ANEXO.test(String(corpo.anexo_url))) {
    throw new FinApiErro('Comprovante inválido: anexe de novo.', { status: 400, code: 'anexo_invalido' });
  }
  const pedido = { op: 'lancar', linha_id: Number(corpo.linha_id) };
  for (const k of CAMPOS_LANCAR) {
    const v = corpo[k];
    if (v === undefined || v === null || (typeof v === 'string' && !v.trim())) continue;
    pedido[k] = typeof v === 'string' ? v.trim() : v;
  }
  if (corpo.confirmo_nao_e_repasse === true) pedido.confirmo_nao_e_repasse = true;
  if (corpo.ok_do_dono === true) pedido.ok_do_dono = true;
  // 22/10/2026 (trava de duplicidade): o dono lança mesmo havendo conta igual, com o motivo (409 conta_igual antes)
  if (corpo.confirmo_outra_despesa === true) pedido.confirmo_outra_despesa = true;
  try {
    const r = await chamarExtrato('POST', pedido, opts);
    avisarMudancaExtrato({ op: 'lancar', linha_id: pedido.linha_id });
    return { ...r, caminho: 'lancar', ligado: true };
  } catch (err) {
    if (err?.code !== 'migracao_pendente' || typeof criarTituloAntigo !== 'function') throw err;
  }
  // Caminho antigo (antes da financeiro-009): cria o título e liga em seguida.
  const t = await criarTituloAntigo();
  const tituloId = Array.isArray(t) ? t[0]?.id : t?.id;
  const avisos = [];
  let ligado = false;
  if (tituloId) {
    try {
      await decidirLinhaExtrato({ linha_id: pedido.linha_id, acao: 'vincular_titulo', titulo_id: tituloId, categoria_sugerida: pedido.categoria }, opts);
      ligado = true;
    } catch {
      avisos.push('O título foi criado, mas a linha do extrato não foi ligada: no Extrato Stone, clique em "É este título".');
    }
  } else {
    avisos.push('O título foi criado, mas sem a identificação de volta: no Extrato Stone, clique em "É este título".');
  }
  avisarMudancaExtrato({ op: 'lancar', linha_id: pedido.linha_id });
  return { ok: true, ja_existia: false, titulo: { id: tituloId || null }, decisao: null, avisos, caminho: 'antigo', ligado };
}

/**
 * O débito pendente do extrato que bate com um título PAGO (mesma conta, mesmo
 * valor, ±2 dias; um só) — para a sugestão "Ligar?" do Contas a Pagar (nunca automática).
 * → { linha: { id, conta_id, dia, valor_cents, historico, favorecido } | null, quantidade,
 *     conta: { id, final_conta, rotulo } | null, motivo: 'ok'|'nao_pago'|'sem_conta'|'nenhum'|'varios'|'ja_ligado' }
 */
export function debitoParaTitulo(titulo_id, opts) {
  if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(String(titulo_id || ''))) {
    return Promise.reject(new FinApiErro('Título sem identificação.', { status: 400, code: 'titulo_inexistente' }));
  }
  return chamarExtrato('GET', { op: 'debito-para-titulo', titulo_id }, opts);
}

/**
 * Rotina da unidade (09/10/2026): títulos ligados a uma linha do extrato
 * (coluna "Banco" do Contas a Pagar). → { vinculos: [{ titulo_id, linha_id, conta_id, dia, valor_cents }] }
 */
export const listarVinculosExtrato = (opts) => chamarExtrato('GET', { op: 'vinculos' }, opts);

/**
 * "Da última vez" na Nova Conta a Pagar (17/10/2026, FIN_ULTIMA_VEZ): o fornecedor (ou o título)
 * digitado × a memória do servidor (todas as contas). Só leitura: o "Usar" da tela só preenche o
 * formulário. conta = 'stone:<final>' | 'caixa' (a do formulário). Textos cortados em 120 letras.
 * → { ultima_vez: { forca, divergente, opcoes: [{ dia, categoria, categoria_nome, descricao, fornecedor,
 *     custo_de, rateio, valor_cents, conta_empresa, conta_unidades, mesma_conta, … }] } | null, motivo, por, conta_id }
 */
export function buscarUltimaVez({ fornecedor = '', titulo = '', unit_id = '', conta = '' } = {}, opts) {
  const curto = (v) => String(v ?? '').replace(/\s+/g, ' ').trim().slice(0, 120);
  return chamarExtrato('GET', { op: 'ultima-vez', fornecedor: curto(fornecedor), titulo: curto(titulo), unit_id, conta }, opts);
}

// ─── 15/10/2026: compra cancelada e reembolsada (utils/finReembolso.js) ────
const RE_ID_LINHA = /^\d{1,18}$/;
const exigirLinha = (id) => {
  if (!RE_ID_LINHA.test(String(id ?? ''))) throw new FinApiErro('Linha do extrato sem identificação (atualize a tela).', { status: 400, code: 'linha_inexistente' });
};
/** "Marcar como reembolsado…": os créditos livres da mesma conta (do dia do débito até 90 dias depois). */
export function listarCreditosReembolso(linha_id, opts) {
  try { exigirLinha(linha_id); } catch (e) { return Promise.reject(e); }
  return chamarExtrato('GET', { op: 'reembolso-creditos', linha_id }, opts);
}
/** Confirma o par débito ↔ crédito (as duas decisões no extrato; a conta a cancelar vem na resposta). */
// 22/10/2026 (lapidação, etapa C, 1.7): conta_escolha 'cancelar' | 'reabrir' (conta paga: o servidor pede a escolha)
export async function confirmarReembolsoExtrato({ debito_linha_id, credito_linha_id, cancelar_conta = false, conta_escolha = null } = {}, opts) {
  exigirLinha(debito_linha_id); exigirLinha(credito_linha_id);
  const r = await chamarExtrato('POST', {
    op: 'reembolso', debito_linha_id: Number(debito_linha_id), credito_linha_id: Number(credito_linha_id), cancelar_conta: cancelar_conta === true,
    ...(conta_escolha === 'cancelar' || conta_escolha === 'reabrir' ? { conta_escolha } : {}),
  }, opts);
  avisarMudancaExtrato({ op: 'reembolso', linha_id: Number(debito_linha_id) });
  return r;
}
/** 22/10/2026 (etapa C, 1.7): reembolso total já confirmado e a conta ainda paga: cancelar ou reabrir (op reembolso-conta). */
export async function resolverContaReembolsoExtrato({ debito_linha_id, conta_escolha } = {}, opts) {
  exigirLinha(debito_linha_id);
  if (conta_escolha !== 'cancelar' && conta_escolha !== 'reabrir') {
    throw new FinApiErro('Escolha "Cancelar a conta lançada" ou "Reabrir como a pagar".', { status: 400, code: 'reembolso_escolha_invalida' });
  }
  const r = await chamarExtrato('POST', { op: 'reembolso-conta', debito_linha_id: Number(debito_linha_id), conta_escolha }, opts);
  avisarMudancaExtrato({ op: 'reembolso-conta', linha_id: Number(debito_linha_id) });
  return r;
}
/** Desfaz o par (as duas decisões), pelo crédito; motivo ≥ 10 letras. */
export async function desfazerReembolsoExtrato({ credito_linha_id, motivo } = {}, opts) {
  exigirLinha(credito_linha_id);
  exigirMotivo(motivo);
  const r = await chamarExtrato('POST', { op: 'reembolso-desfazer', credito_linha_id: Number(credito_linha_id), motivo: String(motivo).trim() }, opts);
  avisarMudancaExtrato({ op: 'reembolso-desfazer', linha_id: Number(credito_linha_id) });
  return r;
}

export default {
  listarAlertas, carregarAlerta, contarAlertas, mudarStatusAlerta, carregarColetas, carregarCatalogo, enfileirarColeta,
  carregarRecebimentos, carregarTaxas, carregarTaxasMes, carregarEntradasAvec, listarContasExtrato, listarArquivosExtrato, listarLinhasExtrato, enviarExtrato,
  decidirLinhaExtrato, lancarDoExtrato, debitoParaTitulo, avisarMudancaExtrato, EVENTO_EXTRATO, listarVinculosExtrato,
  listarCreditosReembolso, confirmarReembolsoExtrato, desfazerReembolsoExtrato, resolverContaReembolsoExtrato,
};
